import { graphql } from 'react-relay';

/**
 * GraphQL operations for the sales-channel switch flow.
 *
 * Operation names carry the `salesChannelSwitchQueries` prefix because relay-compiler requires every operation
 * name to start with its module's basename. The export names stay descriptive.
 *
 * The selections here are copied verbatim from salesChannel.tsx — only the operation names changed.
 */

/* Payment step disabled — the Shopify charge-confirmation mutation is no longer used.
export const confirmShopifyChargeMutation = graphql`
    mutation salesChannelSwitchQueriesConfirmShopifyChargeMutation($input: ShopifyChargeConfirmationInput!) {
        confirmShopifyCharge(input: $input)
    }
`;
*/

export const generateEditSubscriptionTaskMutation = graphql`
    mutation salesChannelSwitchQueriesGenerateEditSubscriptionTaskMutation($input: EditSubscriptionTaskInput!) {
        generateEditSubscriptionTask(input: $input) {
            taskID
        }
    }
`;

export const completePaymentMethodSetupTaskMutation = graphql`
    mutation salesChannelSwitchQueriesCompletePaymentMethodSetupTaskMutation($taskID: Long!) {
        completePaymentMethodSetupTask(taskID: $taskID) {
            taskID
        }
    }
`;

/**
 * First step of the subscription stage, for both the Shopify and the non-Shopify branch. The task decides on its own
 * whether a custom plan is actually needed; its outcome is read back with customPlanOutcomeQuery once it completes.
 */
export const generateCreateCustomPlanForSellerTaskMutation = graphql`
    mutation salesChannelSwitchQueriesGenerateCreateCustomPlanForSellerTaskMutation($input: CreateCustomPlanForSellerTaskInput!) {
        generateCreateCustomPlanForSellerTask(input: $input) {
            taskID
        }
    }
`;

/**
 * Read once, after the custom-plan task completes, to learn which plan the rest of the switch must use:
 * `reusedPlanID` means the seller keeps a plan they already have, `customPlan.planID` means one was just created.
 * Kept separate from pollUnsubscribeTaskQuery so the 5s poll stays as narrow as it is documented to be.
 */
export const customPlanOutcomeQuery = graphql`
    query salesChannelSwitchQueriesCustomPlanOutcomeQuery($id: ID!) {
        productSwitcherTask(id: $id) {
            id
            extraData {
                reusedPlanID
                customPlan {
                    planID
                    financialPreview {
                        isEstimate
                        chargedByThisEndpoint
                        currentPlan {
                            monthlyPrice
                            currencyCode
                        }
                        newPlan {
                            monthlyPrice
                            currencyCode
                        }
                        proration {
                            available
                            estimatedUnusedAmount
                            currencyCode
                            estimatedUnusedAmountInNewCurrency
                            newCurrencyCode
                            requiresManualSettlement
                        }
                        paymentOnPlanChange {
                            mode
                            estimatedCreditApplied
                            estimatedRefundValue
                            estimatedNetAmount
                            currencyCode
                            nextRenewalAtUtc
                            nextRenewalAmount
                        }
                        warnings
                    }
                }
            }
        }
    }
`;

export const generateProductSwitcherTaskMutation = graphql`
    mutation salesChannelSwitchQueriesGenerateProductSwitcherTaskMutation($input: ProductSwitcherTaskInput!) {
        generateProductSwitcherTask(input: $input) {
            taskID
        }
    }
`;

export const generateDeleteShopifyRecurringChargesTaskMutation = graphql`
    mutation salesChannelSwitchQueriesGenerateDeleteShopifyRecurringChargesTaskMutation($input: DeleteShopifyRecurringChargesTaskInput!) {
        generateDeleteShopifyRecurringChargesTask(input: $input) {
            taskID
        }
    }
`;

/* Payment step disabled — the payment-confirmed task mutation is no longer used.
export const createPaymentConfirmedTaskMutation = graphql`
    mutation salesChannelSwitchQueriesCreatePaymentConfirmedTaskMutation($input: PaymentConfirmedTaskInput!) {
        generatePaymentConfirmedTask(input: $input) {
            taskID
        }
    }
`;
*/

/**
 * Polled every 5s while a switch sub-task is in flight. Deliberately a narrow selection: it normalizes into the same
 * Relay records the salesChannelsActive fragment reads, so every extra field here means more snapshot churn on the page.
 */
export const pollUnsubscribeTaskQuery = graphql`
    query salesChannelSwitchQueriesPollUnsubscribeTaskQuery($id: ID!) {
        productSwitcherTask(id: $id) {
            id
            taskID
            state
            lastError
        }
    }
`;

export const checkSellerPlanQuery = graphql`
    query salesChannelSwitchQueriesCheckSellerPlanQuery {
        # Seller plan check intentionally disabled during the sales-channel switch.
        modelsCount(environment: 2)
        hasPodProducts
    }
`;

export const checkShopifyTestStoreQuery = graphql`
    query salesChannelSwitchQueriesCheckShopifyTestStoreQuery($salesChannelID: Long) {
        shopifyAuthEcommerceSettings(salesChannelID: $salesChannelID) {
            isTestStore
        }
    }
`;
